import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../utils/firebase'; // Adjust path to your Firebase config
import { FaArrowLeft } from 'react-icons/fa';
import BgImage from '../../images/winning_ticket.png';
import {useLang} from "../../context/LanguageContext";
import Footer from "../../components/Footer";

const WinningTicketsPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [tickets, setTickets] = useState([]);           // All winning tickets
  const [expandedTicket, setExpandedTicket] = useState(null); // ID of ticket whose dropdown is open
  const [gamesMap, setGamesMap] = useState({});         // { ticketId: [gameObjects] }
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 1) Fetch winning tickets on mount
    const fetchWinningTickets = async () => {
      try {
        setLoadingTickets(true);
        setError(null);

        // Firestore query
        const colRef = collection(db, 'generated');
        const q = query(
          colRef,
          where('status', '==', 'win'),
          orderBy('dateCreated', 'desc'),
          limit(1000)
        );

        const snapshot = await getDocs(q);
        const results = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Condition: totalOdd > 5.0
          if (data.totalOdd > 10.0) {
            // You can add a date filter if needed (like your validateDate logic)
            results.push({
              id: docSnap.id,
              dateCreated: data.dateCreated,
              meciuri: data.meciuri, // array of doc references or doc IDs
              totalOdd: data.totalOdd,
            });
          }
        });

        setTickets(results);
      } catch (err) {
        console.error('Error fetching winning tickets:', err);
        setError('Could not fetch winning tickets.');
      } finally {
        setLoadingTickets(false);
      }
    };

    fetchWinningTickets();
  }, []);

  // 2) Handle toggling the dropdown for a ticket
  const handleToggle = async (ticketId, meciuriArray) => {
    if (expandedTicket === ticketId) {
      // If already expanded, collapse it
      setExpandedTicket(null);
      return;
    }

    // If not expanded, we open it. Check if we already have game data for this ticket
    if (!gamesMap[ticketId]) {
      // Need to fetch all games for meciuri
      try {
        const gameDocs = await fetchGamesForTicket(meciuriArray);
        setGamesMap((prev) => ({ ...prev, [ticketId]: gameDocs }));
      } catch (err) {
        console.error('Error fetching games for this ticket:', err);
      }
    }

    setExpandedTicket(ticketId);
  };

  // 3) Function to fetch games for a single ticket
  const fetchGamesForTicket = async (meciuriArray) => {
    // Each item might look like "meciuri/ABC123" or just "ABC123"
    // We'll handle both cases by removing "meciuri/" if present
    const fetchGameById = async (gamePath) => {
      const pureId = gamePath.replace('meciuri/', '');
      const docRef = doc(db, 'games', pureId);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        throw new Error(`Game doc not found for ID ${pureId}`);
      }
      return { id: snap.id, ...snap.data() };
    };

    const promises = meciuriArray.map((gPath) => fetchGameById(gPath));
    const results = await Promise.all(promises);
    return results;
  };

  // For formatting date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-[#34495e] p-5">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
      >
       <FaArrowLeft />
      <span className="text-white">{t('btn.back')}</span>
      </button>

      <h1 className="text-xl text-white font-bold text-center mt-16 mb-8">{t('txt.winningTickets')}</h1>

      {loadingTickets && <p>{t('txt.winningTicketsLoading')}</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loadingTickets && !error && tickets.length === 0 && (
        <p>{t('txt.winningTicketsNotFound')}</p>
      )}

      {/* Ticket list */}
      <div className="space-y-4">
        {tickets.map((ticket) => {
          const isExpanded = expandedTicket === ticket.id;
          const gamesForTicket = gamesMap[ticket.id] || [];

          return (
            <div key={ticket.id} className="rounded shadow p-4 bg-cover bg-center border-2 border-white"
            style={{ backgroundImage: `url(${BgImage})` }}>
              {/* Ticket overview */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">ID: {ticket.id}</p>
                  <p className="font-bold text-white">
                    {t('txt.totalOdds')}: {ticket.totalOdd.toFixed(2)}
                  </p>
                  <p className="text-sm text-white">
                    {t('txt.created')}: {formatDate(ticket.dateCreated)}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle(ticket.id, ticket.meciuri)}
                  className="bg-[#34495e] text-white px-3 py-1 rounded"
                >
                  {isExpanded ? 'Hide Games' : 'Show Games'}
                </button>
              </div>

              {/* If expanded, show game details */}
              {isExpanded && (
                <div className="mt-3 space-y-2">
                  {gamesForTicket.length === 0 ? (
                    <p>Loading games...</p>
                  ) : (
                    gamesForTicket.map((gm) => (
                      <GameCard key={gm.id} game={gm} />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Footer />
    </div>
  );
};

// A small helper component for rendering game details
function GameCard({ game }) {
  return (
    <div className="border rounded p-2">
      <p className="text-sm font-bold text-white">
        {game.team1} vs {game.team2}
      </p>
      <p className="text-xs text-[#ecf0f1]">
        Day: {game.day} | Hour: {game.hour}
      </p>
      <p className="text-xs text-white">
        Market: {game.marketName} ({game.market}) | Odd: {game.odd}
      </p>
      <p className="text-xs text-[#ecf0f1]">
        Status: {game.status} | Grade: {game.grade}
      </p>
    </div>
  );
}

export default WinningTicketsPage;
